/**
 * 관리자 화면 목록 — **단일 정의**(FR-ADMIN).
 *
 * 왼쪽 메뉴와 AI 도우미가 같은 목록을 본다. 따로 관리하면 화면을 추가할 때마다 두 곳을
 * 고쳐야 하고, 도우미가 없는 화면을 안내하거나 새 화면을 모르는 일이 생긴다.
 *
 * `find` 는 검색·도우미용 별칭이다. 운영자는 화면 이름이 아니라 **고치고 싶은 대상**을 안다
 * ("첨성대", "스마트스토어", "스탬프"…).
 */
export type AdminGroupId = "screen" | "content" | "event" | "tool";

export interface AdminGroup {
  id: AdminGroupId;
  icon: string;
  label: string;
  hint: string;
}

export interface AdminScreen {
  id: string;
  group: AdminGroupId;
  icon: string;
  /** 🧩 그림 아이콘 이름(`/art/ui/act/<art>.webp`) — 2026-09-27. 못 받으면 `icon`(이모지)로. */
  art: string;
  label: string;
  desc: string; // 이 화면에서 무엇을 하는지 (한 줄)
  find: string; // 검색 별칭
}

export const ADMIN_GROUPS: readonly AdminGroup[] = [
  { id: "screen", icon: "📍", label: "화면·공간", hint: "아이가 보는 곳" },
  { id: "content", icon: "📚", label: "콘텐츠", hint: "올리고 쓰는 것" },
  { id: "event", icon: "🎁", label: "행사·연결", hint: "바깥과 이어지는 것" },
  { id: "tool", icon: "🔧", label: "도구·고급", hint: "가끔 쓰는 것" },
];

export const ADMIN_SCREENS: readonly AdminScreen[] = [
  {
    id: "ready",
    group: "screen",
    art: "check",
    icon: "✅",
    label: "출시 준비",
    desc: "아직 안 채운 곳을 한 곳에 모아 보여줘요. 누르면 채우는 화면으로 바로 가요",
    find: "준비 점검 체크리스트 빠진것 비어있음 남은일 할일 출시 오픈",
  },
  {
    id: "home",
    group: "screen",
    art: "home",
    icon: "🏠",
    label: "홈 화면",
    desc: "첫 화면의 글(제목·배지·버튼)과 메인 캐릭터 그림을 한 곳에서 고쳐요",
    find: "랜딩 메인 첫화면 제목 문구 배지 버튼 캐릭터 그레그 병아리 보호자 안내 도움말 개인정보 색 테마 분위기",
  },
  {
    id: "zoneimg",
    group: "screen",
    art: "zoneimg",
    icon: "🏞️",
    label: "체험존 그림",
    desc: "마을을 고르면 그 마을이 실제로 쓰는 그림만 모아 보여줘요. 눌러서 바로 바꾸세요",
    find: "배경 바닥 벽 타일 경주 첨성대 석굴암 카드 그림 NPC 장식 교체",
  },
  {
    id: "zones",
    group: "screen",
    art: "zones",
    icon: "🗺️",
    label: "체험공간 배치",
    desc: "① 마을을 고르고 ② 건물·친구들을 끌어다 놓고 ③ 저장하면 끝!",
    find: "맵 지도 배치 포털 문 이동 오브젝트 위치 충돌 존편집",
  },
  {
    id: "chars",
    group: "screen",
    art: "teddy",
    icon: "🧸",
    label: "캐릭터",
    desc: "친구를 하나 고르면 그 친구의 그림·움직임·키·말을 한 곳에서 고쳐요",
    find: "캐릭터 친구 그레그 병아리 고양이 히어로 탐험가 아바타 옷 룩 그림 움직임 애니메이션 걷기 크기 키 스케일 대사 말풍선 승인",
  },
  {
    id: "book",
    group: "content",
    art: "mybook",
    icon: "📖",
    label: "전자책",
    desc: "① 책을 만들고 ② 페이지마다 그림·소리를 넣고 ③ 저장해요. 카메라 마법(AR)도 여기서!",
    find: "책 페이지 그림책 나레이션 음성 자막 AR 타깃 마법 핫스팟 누르면 눌러 슬라이더 손잡이 낮밤 유물 층 레이어 전시관 3D 모델 3D모델 입체 glb 실물",
  },
  {
    id: "dub",
    group: "content",
    art: "mic",
    icon: "🎙️",
    label: "읽어주기",
    desc: "책을 소리로 읽어 줘요. 빠진 쪽을 한 번에 다 만들고, 어디까지 됐는지 한눈에 봐요",
    find: "읽어주기 더빙 나레이션 음성 목소리 소리 TTS 성우 한국어 영어",
  },
  {
    id: "goods",
    group: "content",
    art: "store", // nav 그림을 admin 폴더에 같은 이름으로 복사해 둔다
    icon: "🛍️",
    label: "굿즈",
    desc: "① 상품 이름·사진·설명을 넣고 ② [게시]하면 상품 페이지가 생겨요",
    find: "상품 굿즈 스토어 판매 가격 스마트스토어 키링",
  },
  {
    id: "arcard",
    group: "content",
    art: "camera",
    icon: "📷",
    label: "AR 카드",
    desc: "경주 유적 카드에 카메라를 비추면 나오는 것들. 인식이 안 될 때 보여줄 영상도 여기서 넣어요",
    find: "AR 증강현실 카드 경주 첨성대 석굴암 천마총 월지 수막새 황리단 영상으로보기 유튜브 폴백 3D 모델 3D모델 입체 glb 실물 돌려보기",
  },
  {
    /**
     * 🩺 **만든 마법이 아이에게 닿고 있나** (2026-09-13). 게시됐나·그림이 오나·카메라가 켜졌나·
     * 못 알아보는 쪽인데 카메라가 켜져 있나 — 지금까지 명령줄 검사(`pnpm check:magic`)만 알던 것을 운영자가 본다.
     * 「25쪽 카메라 끄기를 눌렀는데 됐는지 모르겠다」가 여기서 풀린다.
     */
    id: "magic",
    group: "content",
    art: "wand",
    icon: "🪄",
    label: "마법 점검",
    desc: "책마다 누르면 일어나는 마법이 아이 화면에 잘 나가는지 한눈에 봐요 — 안 올린 것은 여기서 바로 올려요. 보호자 문구도 한 표에서 채워요",
    find: "마법 점검 확인 누르면 애니 AR 카메라 인식 끄기 켜기 3D 수막새 게시 올리기 내보내기 안나옴 안보임 빈칸 그림 무거움 보호자 문구 함께 읽기 질문 초안",
  },
  {
    id: "notice",
    group: "content",
    art: "megaphone",
    icon: "📢",
    label: "마을 소식",
    /**
     * 🔴 **여기에 「기본 화상 방 주소」가 산다.** 이름만 보면 아무도 못 찾는다 —
     * 실제로 「화상」·「회의」로 뒤져서 나오는 화면이 **한 곳도 없었다**(2026-09-03).
     * 화면을 옮기는 건 더 나쁘다: 저장되는 문서가 여기라(`notice_doc`) 옮기면 다른
     * 화면이 모르는 **소식을 통째로 덮어쓴다**(책·굿즈에서 이미 당한 사고).
     * 그러니 **찾는 말**을 늘린다 — 도우미(`adminAssist`)도 이 말로 길을 안내한다.
     */
    desc:
      "공지·소식을 쓰면 웹 [커뮤니티] 화면에 바로 보여요. " +
      "🎥 기본 화상 방 주소(행사·수업이 공통으로 쓰는 방)도 여기서 넣어요",
    find: "공지 소식 커뮤니티 알림 문의 화상 회의 방주소 기본방 웨일온 구글밋 줌 Jitsi 화상회의",
  },
  {
    id: "rally",
    group: "event",
    art: "stamp",
    icon: "🎁",
    label: "행사·랠리",
    desc: "위 탭에서 골라요 — 🎪 행사 일정 · 🎁 랠리 선물(인원·기간·신청 폼·완주자 CSV) · 📍 랠리 지점",
    // 🔴 행사마다 넣는 「회의 주소」가 여기 있다 — 「화상」으로 찾아와야 한다
    find: "행사 이벤트 일정 랠리 스탬프 선물 선착순 완주 지점 좌표 CSV 화상 회의 화상회의 웨일온 구글밋 줌 Jitsi 북토크",
  },
  {
    id: "classes",
    group: "event",
    art: "school",
    icon: "🏫",
    label: "방과후 수업",
    desc: "어느 반이 돌아가고 있는지 한눈에 봐요. 반별 전시장도 여기서 열어요",
    find: "방과후 수업 학교 반 선생님 교실 그림책만들기 전시장 학급 반코드 열쇠 몇명 보존기간 지우기 내려받기",
  },
  {
    id: "classplan",
    group: "event",
    art: "calendar",
    icon: "📅",
    label: "12주 수업 계획",
    desc: "주차마다 무엇을 하는지 적어요. 아이 화면에 「이번 주에 할 일」로 떠요",
    find: "12주 주차 차시 계획 커리큘럼 교재 진도 수업내용 몇주차 이번주 결석 교과서 쪽수",
  },
  {
    id: "qr",
    group: "event",
    art: "qr",
    icon: "🔗",
    label: "QR 코드",
    desc: "책·굿즈에 인쇄된 QR 이 어디로 갈지 정해요. 페이지가 바뀌면 여기서 목적지만 고치면 돼요",
    find: "QR 큐알 NFC 인쇄 코드 링크 리다이렉트 목적지",
  },
  {
    id: "aikb",
    group: "content",
    art: "shelf",
    icon: "📚",
    label: "AI 자료",
    desc: "마을 친구가 근거로 삼을 글을 올려요. 학습이 아니라 「찾아서 보여 주기」라 지우면 사라져요",
    find: "AI 도슨트 자료 지식 학습 데이터 첨부 문서 근거 RAG 해설 안내 챗봇 대화",
  },
  {
    id: "custom",
    group: "content",
    art: "palette",
    icon: "➕",
    label: "직접 만들기",
    desc: "새 모자·소품·친구·배경을 내 그림으로 만들어요. 이름 짓고 그림만 올리면 끝!",
    find: "추가 새로 만들기 커스텀 직접 업로드 내그림 모자 소품 장식 캐릭터 친구 NPC 배경 꾸미기 리소스",
  },
  {
    id: "assets",
    group: "tool",
    art: "gallery",
    icon: "🖼️",
    label: "그림 전체 목록",
    desc: "올릴 수 있는 자리를 전부 모아 봐요(고급). 평소엔 [🏞️ 체험존 그림]·[🧸 캐릭터]가 더 쉬워요",
    find: "슬롯 전체 이미지 업로드 히어로 파츠 아바타 타일셋",
  },
  {
    id: "anim",
    group: "tool",
    art: "film",
    icon: "🎞️",
    label: "움직임 만들기",
    desc: "그림 여러 장을 이어붙여 움직이게 만들어요(고급 · 캐릭터는 [🧸 캐릭터]가 더 쉬워요)",
    find: "애니메이션 스트립 프레임 움직임 걷기 fps",
  },
  {
    id: "artilt",
    group: "tool",
    art: "tilt",
    icon: "📐",
    label: "카메라 기울기",
    desc: "책을 비스듬히 들면 마법 그림도 같이 기울어요(카메라 AR). 폰에서 보고 켜 주세요",
    find: "AR 카메라 기울기 원근 각도 3D 입체 따라 기울 마법 트래킹",
  },
  {
    id: "stats",
    group: "tool",
    art: "chart",
    icon: "📊",
    label: "방문자수",
    desc: "얼마나 왔고 어느 곳을 많이 봤는지 숫자로 보여줘요. 누가 왔는지는 저장하지 않아요",
    find: "통계 방문자 방문자수 집계 숫자 인기 조회수 몇명 분석 기간 날짜",
  },
  {
    id: "uifeat",
    group: "tool",
    art: "toggle",
    icon: "🎚️",
    label: "수업 모드·화면 보이기",
    desc: "방과후 수업 모드인지 일반 모드인지 정하고, 아이 화면의 메뉴·기능을 켜고 꺼요",
    find: "모드 방과후 수업모드 일반모드 학교모드 전환 바꾸기 보이기 숨기기 감추기 끄기 켜기 메뉴 기능 굿즈숨김 커뮤니티숨김 학교 수업 행사 부스 타겟 대상",
  },
  {
    id: "env",
    group: "tool",
    art: "tools",
    icon: "⚙️",
    label: "설정·백업",
    desc: "지금 모습을 통째로 저장해 두고, 나중에 되돌릴 수 있어요. AI 열쇠도 여기서 관리",
    find: "백업 스냅샷 복원 복구 되돌리기 열쇠 키 비밀번호 토큰 API 설정",
  },
];

/** 있는 화면인가 — AI 가 준 이동 대상도 외부 입력이다(불변식 7). */
/**
 * 🔎 **왼쪽 메뉴 「어디서 바꾸지?」 검색이 이 화면을 찾나** — 이름·설명·별칭(find)을 훑는다(대소문자 무시).
 * 메뉴(App.tsx)와 검사가 **같은 판정**을 쓴다(2026-10-03 — 「3D 모델 칸이 메뉴에 안 보여」: 3D 로 찾으면
 * 올리는 곳이 아니라 점검 화면만 나왔다).
 */
export function adminScreenMatches(s: Pick<AdminScreen, "label" | "desc" | "find">, query: string): boolean {
  const term = query.trim().toLowerCase();
  return !term || `${s.label} ${s.desc} ${s.find}`.toLowerCase().includes(term);
}

export function isAdminScreenId(v: unknown): v is string {
  return typeof v === "string" && ADMIN_SCREENS.some((s) => s.id === v);
}

export function adminScreen(id: string): AdminScreen | undefined {
  return ADMIN_SCREENS.find((s) => s.id === id);
}
